import React, { useState } from 'react';
import { exportCoachSessionPDF, exportClientSessionPDF, exportProgressReportPDF } from '../utils/pdfExport';

export function SessionExportModal({ session, client, practiceName, onClose }) {
    const [coachMessage, setCoachMessage] = useState('');
    const [clientOptions, setClientOptions] = useState({
        overview: true,
        themes: true,
        breakthroughs: true,
        commitments: true,
        goals: true,
        stepsForward: true,
        coachMessage: true,
    });
    const [activeTab, setActiveTab] = useState('client');

    const toggleOption = (key) => {
        setClientOptions(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const clientSections = [
        { key: 'overview', label: 'Session overview' },
        { key: 'themes', label: 'What we explored (key themes)' },
        { key: 'breakthroughs', label: 'Breakthrough moments (starred only)' },
        { key: 'commitments', label: 'Your commitments (action items)' },
        { key: 'goals', label: 'Session goals' },
        { key: 'stepsForward', label: 'Steps forward' },
        { key: 'coachMessage', label: 'Personal note from coach' },
    ];

    return (
        <div style={styles.overlay} onClick={onClose}>
            <div style={styles.modal} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <div style={styles.modalTitle}>Export session summary</div>
                    <button style={styles.closeBtn} onClick={onClose}>×</button>
                </div>

                <div style={styles.tabs}>
                    <button
                        style={{ ...styles.tab, ...(activeTab === 'client' ? styles.tabActive : {}) }}
                        onClick={() => setActiveTab('client')}
                    >
                        Client copy
                    </button>
                    <button
                        style={{ ...styles.tab, ...(activeTab === 'coach' ? styles.tabActive : {}) }}
                        onClick={() => setActiveTab('coach')}
                    >
                        Coach copy
                    </button>
                </div>

                {activeTab === 'client' && (
                    <div style={styles.modalBody}>
                        <div style={styles.sectionLabel}>Include in client copy</div>
                        {clientSections.map(section => (
                            <div key={section.key} style={styles.checkRow}>
                                <div
                                    style={{ ...styles.checkbox, ...(clientOptions[section.key] ? styles.checkboxChecked : {}) }}
                                    onClick={() => toggleOption(section.key)}
                                >
                                    {clientOptions[section.key] && <span style={styles.checkmark}>✓</span>}
                                </div>
                                <div style={styles.checkLabel}>{section.label}</div>
                            </div>
                        ))}

                        {clientOptions.coachMessage && (
                            <div style={styles.messageSection}>
                                <div style={styles.sectionLabel}>Personal note to client</div>
                                <textarea
                                    style={styles.textarea}
                                    placeholder="Write a personal note to your client — something encouraging, specific to this session, or looking ahead..."
                                    value={coachMessage}
                                    onChange={e => setCoachMessage(e.target.value)}
                                />
                            </div>
                        )}

                        <button
                            style={styles.exportBtn}
                            onClick={() => { exportClientSessionPDF(session, client, practiceName, clientOptions, coachMessage); onClose(); }}
                        >
                            Export client PDF
                        </button>
                    </div>
                )}

                {activeTab === 'coach' && (
                    <div style={styles.modalBody}>
                        <div style={styles.coachNote}>
                            The coach copy includes everything — full summary, coaching observations, goal assessments, breakthrough moments, and your reflection notes. Marked confidential.
                        </div>
                        <button
                            style={styles.exportBtn}
                            onClick={() => { exportCoachSessionPDF(session, client, practiceName); onClose(); }}
                        >
                            Export coach PDF
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export function ProgressReportExportModal({ client, sessions, practiceName, transformationArc, onClose }) {
    const [coachMessage, setCoachMessage] = useState('');
    const [options, setOptions] = useState({
        arc: !!transformationArc?.arc,
        achievedGoals: true,
        activeGoals: true,
        breakthroughs: true,
        coachMessage: true,
    });

    const toggleOption = (key) => {
        setOptions(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const sections = [
        { key: 'arc', label: 'Transformation journey narrative', disabled: !transformationArc?.arc },
        { key: 'achievedGoals', label: 'Goals achieved' },
        { key: 'activeGoals', label: 'Goals in progress' },
        { key: 'breakthroughs', label: 'Breakthrough moments (starred)' },
        { key: 'coachMessage', label: 'Personal note from coach' },
    ];

    return (
        <div style={styles.overlay} onClick={onClose}>
            <div style={styles.modal} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <div style={styles.modalTitle}>Export progress report</div>
                    <button style={styles.closeBtn} onClick={onClose}>×</button>
                </div>

                <div style={styles.modalBody}>
                    <div style={styles.sectionLabel}>Include in report</div>
                    {sections.map(section => (
                        <div key={section.key} style={{ ...styles.checkRow, opacity: section.disabled ? 0.4 : 1 }}>
                            <div
                                style={{ ...styles.checkbox, ...(options[section.key] && !section.disabled ? styles.checkboxChecked : {}) }}
                                onClick={() => !section.disabled && toggleOption(section.key)}
                            >
                                {options[section.key] && !section.disabled && <span style={styles.checkmark}>✓</span>}
                            </div>
                            <div style={styles.checkLabel}>
                                {section.label}
                                {section.disabled && <span style={styles.disabledNote}> — generate transformation arc first</span>}
                            </div>
                        </div>
                    ))}

                    {options.coachMessage && (
                        <div style={styles.messageSection}>
                            <div style={styles.sectionLabel}>Personal note to client</div>
                            <textarea
                                style={styles.textarea}
                                placeholder="Write a closing note — celebrating their progress, looking ahead to what's next..."
                                value={coachMessage}
                                onChange={e => setCoachMessage(e.target.value)}
                            />
                        </div>
                    )}

                    <button
                        style={styles.exportBtn}
                        onClick={() => { exportProgressReportPDF(client, sessions, practiceName, transformationArc, options, coachMessage); onClose(); }}
                    >
                        Export progress report PDF
                    </button>
                </div>
            </div>
        </div>
    );
}

const styles = {
    overlay: { position: 'fixed', inset: 0, background: 'rgba(45,58,53,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, fontFamily: "'Figtree',sans-serif" },
    modal: { background: 'white', borderRadius: '16px', width: '480px', maxWidth: '95vw', maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,0.15)' },
    modalHeader: { padding: '20px 24px', borderBottom: '1px solid #D8E0DC', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 },
    modalTitle: { fontFamily: 'Georgia,serif', fontSize: '20px', fontWeight: '300', fontStyle: 'italic', color: '#2D3A35' },
    closeBtn: { background: 'none', border: 'none', fontSize: '24px', color: '#8C9E98', cursor: 'pointer', lineHeight: 1 },
    tabs: { display: 'flex', gap: '4px', padding: '12px 24px', background: '#F8F5F0', borderBottom: '1px solid #D8E0DC', flexShrink: 0 },
    tab: { flex: 1, padding: '8px 12px', borderRadius: '7px', border: 'none', background: 'none', fontFamily: "'Figtree',sans-serif", fontSize: '13px', color: '#8C9E98', cursor: 'pointer' },
    tabActive: { background: 'white', color: '#2D3A35', fontWeight: '500', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' },
    modalBody: { padding: '20px 24px', overflowY: 'auto', flex: 1 },
    sectionLabel: { fontSize: '11px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#8C9E98', marginBottom: '12px' },
    checkRow: { display: 'flex', alignItems: 'center', gap: '12px', padding: '9px 0', borderBottom: '1px solid #F0F4F2' },
    checkbox: { width: '20px', height: '20px', borderRadius: '5px', border: '1.5px solid #D8E0DC', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, transition: 'all 0.15s' },
    checkboxChecked: { background: '#5C7A6B', borderColor: '#5C7A6B' },
    checkmark: { color: 'white', fontSize: '12px', fontWeight: '700' },
    checkLabel: { fontSize: '13.5px', color: '#2D3A35' },
    disabledNote: { fontSize: '11px', color: '#8C9E98', fontStyle: 'italic' },
    messageSection: { marginTop: '20px' },
    textarea: { width: '100%', padding: '11px 14px', borderRadius: '8px', border: '1px solid #D8E0DC', background: '#F8F5F0', fontFamily: "'Figtree',sans-serif", fontSize: '13.5px', color: '#2D3A35', outline: 'none', minHeight: '100px', resize: 'vertical', boxSizing: 'border-box', marginTop: '8px' },
    coachNote: { background: '#EBF0ED', borderRadius: '8px', padding: '14px 16px', fontSize: '13.5px', color: '#4A5A54', lineHeight: 1.6, marginBottom: '20px' },
    exportBtn: { width: '100%', padding: '12px', borderRadius: '8px', background: '#5C7A6B', border: 'none', color: 'white', fontFamily: "'Figtree',sans-serif", fontSize: '14px', fontWeight: '500', cursor: 'pointer', marginTop: '20px' },
};